import React, { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { CSSTransition } from 'react-transition-group';

import cls from 'classnames';

import throttle from 'lodash/throttle';

import CrossIcon from '@ott/icon-cross';
import { getLayoutContextValue } from '@ott/layout-context';

import { Constants, HoverHandlers } from '../../Tooltip';
import calculatePlacementStyles from '../../helpers/calculatePlacementStyles';
import { ANIMATION_TIMEOUT } from '../../helpers/constants';
import { getAnimationClassNames } from '../../helpers/getAnimationClassnames';
import { Presets, Placement } from '../../helpers/types';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import useWebApi from '../../hooks/useWebApi';

import styles from './Content.scss';

export type ContentChildrenClassNames = {
  cross?: string;
  contentWrapper?: string;
};

type ContentProps = {
  baseLocator: Constants['baseLocator'];
  childrenClassNames: ContentChildrenClassNames;
  className?: string;
  closeTooltip: () => void;
  content: ReactNode;
  contentHoverHandlers: HoverHandlers;
  contentMargin: number;
  hasCloser: boolean;
  hasOuterControl: boolean;
  isOpen: boolean;
  pagePadding: number;
  placement: Placement;
  preset?: Presets;
  specificLocator?: string;
  targetRef: RefObject<HTMLElement>;
};

export const Content = (props: ContentProps) => {
  const {
    baseLocator,
    childrenClassNames,
    className,
    closeTooltip,
    content,
    contentHoverHandlers,
    contentMargin,
    hasCloser,
    hasOuterControl,
    isOpen,
    pagePadding,
    placement,
    preset,
    specificLocator,
    targetRef,
  } = props;

  const [placementStyles, setPlacementStyles] = useState({});
  const [isVisible, setIsVisible] = useState(false);
  const previousViewportWidth = useRef<number>(getLayoutContextValue().viewportWidth);

  const contentRef = useRef<HTMLDivElement>(null);

  const applyPlacementStyles = () => {
    if (!contentRef.current || !targetRef.current) {
      return;
    }

    const contentRect = contentRef.current.getBoundingClientRect();
    const triggerRect = targetRef.current.getBoundingClientRect();

    const placementStyles = calculatePlacementStyles({
      contentMargin,
      contentRect,
      pagePadding,
      placement,
      triggerRect,
    });

    setPlacementStyles(placementStyles);
  };

  useEffect(() => {
    // В мобильных браузерах часто возникает неожиданный вертикальный ресайз
    // когда при скролле появляется панель управления браузера,
    // поэтому делаем репозиционирование только на горизонтальный ресайз
    const applyPlacementStylesOnHorizontalResizeOnly = () => {
      const currentViewPortWidth = getLayoutContextValue().viewportWidth;
      const isHorizontalResize = previousViewportWidth.current !== currentViewPortWidth;

      if (isHorizontalResize) {
        applyPlacementStyles();
        previousViewportWidth.current = currentViewPortWidth;
      }
    };

    const throttledApplyPlacementStyles = throttle(applyPlacementStylesOnHorizontalResizeOnly, 300);

    if (isOpen) {
      setIsVisible(true);
      applyPlacementStyles();

      window.addEventListener('resize', throttledApplyPlacementStyles);
    } else {
      setIsVisible(false);
    }

    return () => {
      window.removeEventListener('resize', throttledApplyPlacementStyles);
    };
  }, [contentRef, targetRef, isOpen]);

  useOnClickOutside({
    contentRef,
    targetRef,
    handler: closeTooltip,
    isOpen,
    isDisabled: hasOuterControl,
  });

  const { document } = useWebApi();

  if (!document) {
    return null;
  }

  const presetClassName = preset ? styles[preset] : null;

  return ReactDOM.createPortal(
    <CSSTransition
      classNames={getAnimationClassNames(placement)}
      in={isOpen}
      timeout={ANIMATION_TIMEOUT}
      unmountOnExit={true}
      onExited={() => setPlacementStyles({})}
    >
      <div
        {...contentHoverHandlers}
        onClick={(event) => event.stopPropagation()}
        style={placementStyles}
        className={cls(styles.contentWrapper, childrenClassNames.contentWrapper)}
        data-locator={cls(specificLocator, `${baseLocator}-content`)}
      >
        <div
          className={cls(styles.content, presetClassName, className, {
            [styles.isHidden]: !isVisible,
          })}
          ref={contentRef}
        >
          {hasCloser && (
            <span
              onClick={closeTooltip}
              className={cls(styles.cross, childrenClassNames.cross)}
              data-locator={cls(specificLocator, `${baseLocator}-cross`)}
            >
              <CrossIcon className={styles.crossIcon} />
            </span>
          )}
          {content}
        </div>
      </div>
    </CSSTransition>,
    document.body
  );
};
